/**
 * Dashboard parameter binding (S10).
 *
 * Renderer side: `deriveTileParameters` turns declared parameters + the
 * dashboard's current values into the wire `parameters` array on
 * `TileExecutionRequest` — the RAW SQL travels untouched (the scheduleTrust
 * hash binds raw persisted SQL to the raw request query, S7-R1 invariant).
 *
 * Main side: `substituteParams` (called from composeQuery AFTER the policy
 * decision) rewrites live `{{name}}` tokens to driver placeholders and
 * produces the ordered driver-typed parameter list. Kusto (mgmt commands
 * cannot take query parameters) inlines engine-escaped literals instead —
 * the plan-sanctioned fallback.
 *
 * "All" on a multi-select list ELIDES the whole predicate: the canonical
 * shape `expr IN ({{param}})` (kusto `expr in ({{param}})`) is replaced by
 * `1=1`/`true`. Any other shape errors loudly — never silent.
 */
import type { BoundParams, DashboardParameter, ParamValueType, TimeRangeValue } from '../types/dashboardTypes';
import type { WireDashboardFilter } from '../types/dashboardRuntime';
import { formatLiteral } from './paramLiterals';
import { findPlaceholders, placeholderNames } from './sqlPlaceholders';

/** Sentinel stored in BoundParams when an includeAll list is set to "All". */
export const PARAM_ALL = '$__all';
export const DASHBOARD_FILTERS_NAME = 'dashboard_filters';

export type WireParam = { name: string; type: ParamValueType; value: string | string[]; all?: boolean };
export type DriverParam = { name: string; type: ParamValueType; value: string };

const DAY_MS = 86_400_000;

/** [start, end) — UTC-anchored. Fixed windows end at `now`; custom date-only
 * bounds snap to 00:00Z with the end bound EXCLUSIVE (to-date + 1 day). */
export function resolveTimeRange(
  def: Extract<DashboardParameter, { kind: 'time-range' }>,
  value: BoundParams[string] | undefined,
  now: Date,
): { start: string; end: string } {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const range = value as TimeRangeValue;
    const dateOnly = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
    const start = dateOnly(range.from) ? `${range.from}T00:00:00.000Z` : new Date(range.from).toISOString();
    const end = dateOnly(range.to)
      ? new Date(Date.parse(`${range.to}T00:00:00.000Z`) + DAY_MS).toISOString()
      : new Date(range.to).toISOString();
    return { start, end };
  }
  const days = typeof value === 'number' ? value : def.defaultDays;
  const end = now.toISOString();
  const start = new Date(now.getTime() - days * DAY_MS).toISOString();
  return { start, end };
}

export type DeriveResult = { ok: true; parameters: WireParam[] } | { ok: false; missing: string[] };

/** Wire params for the placeholders THIS tile actually uses. Unbound names
 * (no declaration, or no value and no default) are reported together. */
export function deriveTileParameters(args: {
  sql: string;
  engine: string;
  parameters: DashboardParameter[];
  values: BoundParams;
  now?: Date;
}): DeriveResult {
  const now = args.now ?? new Date();
  const used = placeholderNames(args.sql, args.engine).filter((n) => n !== DASHBOARD_FILTERS_NAME);
  const timeRange = args.parameters.find((p) => p.kind === 'time-range');
  const byId = new Map(args.parameters.filter((p) => p.kind !== 'time-range').map((p) => [p.id, p]));
  const out: WireParam[] = [];
  const missing: string[] = [];

  for (const name of used) {
    if (name === 'range_start' || name === 'range_end') {
      if (!timeRange) {
        missing.push(name);
        continue;
      }
      const { start, end } = resolveTimeRange(timeRange, args.values.range, now);
      out.push({ name, type: 'date', value: name === 'range_start' ? start : end });
      continue;
    }
    const def = byId.get(name);
    if (!def) {
      missing.push(name);
      continue;
    }
    const type: ParamValueType = def.valueType ?? 'string';
    const raw = args.values[name] ?? def.defaultValue;
    if (raw === undefined || (typeof raw === 'object' && !Array.isArray(raw))) {
      missing.push(name);
      continue;
    }
    const multi = 'multi' in def && def.multi === true;
    if (multi) {
      const arr = Array.isArray(raw) ? raw.map(String) : [String(raw)];
      if ('includeAll' in def && def.includeAll && arr.includes(PARAM_ALL)) {
        out.push({ name, type, value: [], all: true });
      } else if (arr.length === 0) {
        missing.push(name);
      } else {
        out.push({ name, type, value: arr });
      }
    } else {
      const scalar = Array.isArray(raw) ? raw[0] : raw;
      if (scalar === undefined || String(scalar) === PARAM_ALL) {
        if ('includeAll' in def && def.includeAll) out.push({ name, type, value: [], all: true });
        else missing.push(name);
      } else {
        out.push({ name, type, value: String(scalar) });
      }
    }
  }
  return missing.length > 0 ? { ok: false, missing: [...new Set(missing)] } : { ok: true, parameters: out };
}

export type SubstituteResult =
  | { ok: true; sql: string; driverParams: DriverParam[] }
  | { ok: false; error: string; unresolved?: string };

const placeholderText = (engine: string, index: number): string => {
  switch (engine) {
    case 'mssql':
      return `@p${index}`;
    case 'pgsql':
      return `$${index + 1}`;
    case 'oracle':
      return `:p${index}`;
    default:
      return '?'; // mysql, sqlite
  }
};

/** Engines whose tile path inlines literals instead of binding (fallback):
 * kusto mgmt commands can't take query parameters; the e2e mock adapter has
 * no bind support and matches on the substituted SQL text. */
const LITERAL_ENGINES = new Set(['kusto', 'mock']);

const IN_SHAPE = 'the "All" option needs the canonical shape `expr IN ({{param}})`';

// ---------------------------------------------------------------------------
// Cross-filter predicate composition (S11) — fills the {{dashboard_filters}}
// slot with AND-joined typed predicates. targetExpression is validated as a
// STRICT bare identifier (injection-impossible) and engine-quoted the same
// way Table Explorer quotes metadata-derived column names.
// ---------------------------------------------------------------------------

const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_$]*$/;
const DAY_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function quoteFilterIdentifier(engine: string, name: string): string {
  switch (engine) {
    case 'mssql':
      return `[${name}]`;
    case 'mysql':
      return `\`${name}\``;
    case 'kusto':
      return `['${name}']`;
    case 'mock':
      // The e2e mock matches substituted TEXT — keep it readable/stable.
      return name;
    default:
      return `"${name}"`; // pgsql, oracle, sqlite
  }
}

/**
 * Date granularity is ENCODED BY THE PRODUCER (C3): chart shaping emits a
 * bare `YYYY-MM-DD` for day-grouped date axes and a full ISO instant for
 * time-grained ones — no midnight heuristics here (a genuine 00:00 bucket on
 * a time-grained chart must stay equality). Day-only values compose as
 * [day, day+1) ranges, UTC-anchored (S10's custom-range convention); any
 * other parseable value keeps equality; anything else errors loudly.
 */
function dateBounds(value: string): { start: string; end: string } | { eq: string } | null {
  if (DAY_ONLY_RE.test(value)) {
    const startMs = Date.parse(`${value}T00:00:00.000Z`);
    return { start: new Date(startMs).toISOString(), end: new Date(startMs + DAY_MS).toISOString() };
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return { eq: parsed.toISOString() };
}

type PredicateSink = {
  literalMode: boolean;
  engine: string;
  driverParams: DriverParam[];
};

/** One value slot: driver placeholder, or an escaped literal on fallback engines. */
function valueText(sink: PredicateSink, type: ParamValueType, value: string): { ok: true; text: string } | { ok: false; error: string } {
  if (sink.literalMode) {
    const lit = formatLiteral(sink.engine, type, value);
    if (!lit.ok) return { ok: false, error: lit.error };
    return { ok: true, text: lit.literal };
  }
  const text = placeholderText(sink.engine, sink.driverParams.length);
  sink.driverParams.push({ name: `p${sink.driverParams.length}`, type, value });
  return { ok: true, text };
}

export function buildFilterPredicate(
  engine: string,
  filters: WireDashboardFilter[],
  sink: PredicateSink,
): { ok: true; text: string } | { ok: false; error: string } {
  const kusto = engine === 'kusto';
  const parts: string[] = [];
  for (const filter of filters) {
    // C3-R2 (Codex 5): SQL targets may be QUALIFIED (`o.region`, up to
    // schema.table.column) so joins with ambiguous column names stay
    // unambiguous — every dot-part is still strict-identifier validated and
    // engine-quoted individually. Kusto columns are never qualified.
    const idParts = kusto ? [filter.targetExpression] : filter.targetExpression.split('.');
    if (idParts.length > 3 || !idParts.every((p) => IDENTIFIER_RE.test(p))) {
      return {
        ok: false,
        error: `Cross-filter target "${filter.targetExpression}" is not a plain column identifier`,
      };
    }
    const col = idParts.map((p) => quoteFilterIdentifier(engine, p)).join('.');
    if (filter.value === null) {
      parts.push(kusto ? `isnull(${col})` : `${col} IS NULL`);
      continue;
    }
    if (filter.valueType === 'date') {
      const bounds = dateBounds(filter.value);
      if (!bounds) {
        return { ok: false, error: `Cross-filter value "${filter.value}" is not a recognizable date` };
      }
      if ('eq' in bounds) {
        const eq = valueText(sink, 'date', bounds.eq);
        if (!eq.ok) return eq;
        parts.push(`${col} ${kusto ? '==' : '='} ${eq.text}`);
      } else {
        const start = valueText(sink, 'date', bounds.start);
        if (!start.ok) return start;
        const end = valueText(sink, 'date', bounds.end);
        if (!end.ok) return end;
        parts.push(`${col} >= ${start.text} ${kusto ? 'and' : 'AND'} ${col} < ${end.text}`);
      }
      continue;
    }
    const val = valueText(sink, filter.valueType, filter.value);
    if (!val.ok) return val;
    parts.push(`${col} ${kusto ? '==' : '='} ${val.text}`);
  }
  const joiner = kusto ? ' and ' : ' AND ';
  return { ok: true, text: parts.map((p) => `(${p})`).join(joiner) };
}

/** Widen an occurrence to the whole `expr IN ( {{param}} )` predicate. */
function elideRange(sql: string, start: number, end: number): { start: number; end: number } | null {
  let close = end;
  while (close < sql.length && /\s/.test(sql[close])) close += 1;
  if (sql[close] !== ')') return null;
  let i = start - 1;
  while (i >= 0 && /\s/.test(sql[i])) i -= 1;
  if (sql[i] !== '(') return null;
  i -= 1;
  while (i >= 0 && /\s/.test(sql[i])) i -= 1;
  const inEnd = i + 1;
  if (!/in/i.test(sql.slice(i - 1, inEnd)) || (i - 2 >= 0 && /[\w$]/.test(sql[i - 2]))) return null;
  i -= 2;
  while (i >= 0 && /\s/.test(sql[i])) i -= 1;
  const lhsEnd = i + 1;
  // Consume the lhs: balanced parens (function calls), quoted segments, dotted identifiers.
  let consumed = false;
  while (i >= 0) {
    if (sql[i] === ')') {
      let depth = 1;
      i -= 1;
      while (i >= 0 && depth > 0) {
        if (sql[i] === ')') depth += 1;
        else if (sql[i] === '(') depth -= 1;
        i -= 1;
      }
      consumed = true;
      continue;
    }
    if (sql[i] === '"' || sql[i] === '`' || sql[i] === ']') {
      const open = sql[i] === ']' ? '[' : sql[i];
      i -= 1;
      while (i >= 0 && sql[i] !== open) i -= 1;
      i -= 1;
      consumed = true;
      continue;
    }
    if (/[\w$.]/.test(sql[i])) {
      while (i >= 0 && /[\w$.]/.test(sql[i])) i -= 1;
      consumed = true;
      continue;
    }
    break;
  }
  if (!consumed) return null;
  const lhsStart = i + 1;
  // `x NOT IN ({{p}})` — the back-scan grabs NOT as if it were the lhs — and
  // `NOT x IN ({{p}})` are both ambiguous under "All": error, never guess.
  if (/^not$/i.test(sql.slice(lhsStart, lhsEnd))) return null;
  if (/\bnot\s*$/i.test(sql.slice(0, lhsStart))) return null;
  return { start: lhsStart, end: close + 1 };
}

/** Rewrite live placeholders to driver placeholders (or literals on fallback
 * engines) and collect the ordered driver-typed parameter list. The
 * `{{dashboard_filters}}` slot resolves to the composed cross-filter
 * predicate when `filters` are present, else the neutral tautology (S7-R1). */
export function substituteParams(
  sql: string,
  engine: string,
  params: WireParam[],
  filters: WireDashboardFilter[] = [],
): SubstituteResult {
  const byName = new Map(params.map((p) => [p.name, p]));
  const tautology = engine === 'kusto' ? 'true' : '1=1';
  const literalMode = LITERAL_ENGINES.has(engine);
  const edits: Array<{ start: number; end: number; text: string }> = [];
  const driverParams: DriverParam[] = [];
  const sink: PredicateSink = { literalMode, engine, driverParams };
  let filtersSlotSeen = false;

  for (const occ of findPlaceholders(sql, engine)) {
    if (occ.name === DASHBOARD_FILTERS_NAME) {
      filtersSlotSeen = true;
      if (filters.length === 0) {
        edits.push({ start: occ.start, end: occ.end, text: tautology });
        continue;
      }
      const predicate = buildFilterPredicate(engine, filters, sink);
      if (!predicate.ok) return { ok: false, error: predicate.error };
      edits.push({ start: occ.start, end: occ.end, text: predicate.text });
      continue;
    }
    const param = byName.get(occ.name);
    if (!param) {
      return { ok: false, error: `Unresolved placeholder {{${occ.name}}} in tile query`, unresolved: occ.name };
    }
    if (param.all) {
      const range = elideRange(sql, occ.start, occ.end);
      if (!range) {
        return { ok: false, error: `Parameter "${occ.name}" is set to All but ${IN_SHAPE}` };
      }
      edits.push({ ...range, text: tautology });
      continue;
    }
    const values = Array.isArray(param.value) ? param.value : [param.value];
    const parts: string[] = [];
    for (const value of values) {
      if (literalMode) {
        const lit = formatLiteral(engine, param.type, value);
        if (!lit.ok) return { ok: false, error: `Parameter "${occ.name}": ${lit.error}` };
        parts.push(lit.literal);
      } else {
        parts.push(placeholderText(engine, driverParams.length));
        driverParams.push({ name: `p${driverParams.length}`, type: param.type, value });
      }
    }
    edits.push({ start: occ.start, end: occ.end, text: parts.join(', ') });
  }

  // C3 finding 4: a slot inside a comment/string is inert to the tokenizer —
  // composing "successfully" with zero live slots would run UNFILTERED while
  // the tile presents as filtered. Fail loudly instead.
  if (filters.length > 0 && !filtersSlotSeen) {
    return {
      ok: false,
      error:
        'Cross-filters are active but the query has no {{dashboard_filters}} slot outside comments and strings',
    };
  }

  let composed = '';
  let cursor = 0;
  for (const edit of edits) {
    composed += sql.slice(cursor, edit.start) + edit.text;
    cursor = edit.end;
  }
  composed += sql.slice(cursor);
  return { ok: true, sql: composed, driverParams };
}

/** Params to re-fetch when `changedId` changes — dependency order, cycles cut. */
export function cascadeInvalidationOrder(parameters: DashboardParameter[], changedId: string): string[] {
  const queryLists = parameters.filter((p) => p.kind === 'query-list');
  const out: string[] = [];
  const visited = new Set<string>([changedId]);
  let frontier = [changedId];
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const p of queryLists) {
      if (visited.has(p.id)) continue;
      if (p.dependsOn?.some((dep) => frontier.includes(dep))) {
        visited.add(p.id);
        out.push(p.id);
        next.push(p.id);
      }
    }
    frontier = next;
  }
  return out;
}
